import { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './table.module.scss';

const PAGE_SIZE = 10;

const getProgressColor = value => {
  if (value >= 50) return 'high';
  if (value > 0) return 'mid';
  return 'zero';
};

const Table = ({ title, subTitle, headers, data, showTotals = false }) => {
  const [page, setPage] = useState(0);

  const formatPercentage = percentage => {
    if (percentage === 0) return '0%';
    if (percentage % 1 === 0) return `${percentage}%`;
    return `${percentage.toFixed(1)}%`;
  };

  const isEmpty = !data || data.length === 0;
  const totalPages = data ? Math.ceil(data.length / PAGE_SIZE) : 0;
  const pagedData = data ? data.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) : [];

  const totals =
    showTotals && !isEmpty
      ? headers.reduce((acc, col) => {
          if (col.key === headers[0].key) {
            acc[col.key] = 'Total';
          } else if (col.isSummable === false) {
            acc[col.key] = '';
          } else if (col.isPercentage) {
            const sum = data.reduce((s, row) => s + (Number(row[col.key]) || 0), 0);
            acc[col.key] = data.length > 0 ? sum / data.length : 0;
          } else {
            const firstVal = data[0]?.[col.key];
            const strVal = String(firstVal || '');
            const currencyMatch = strVal.match(/^([^0-9.-]*)(-?[\d.]+)(.*)$/);
            if (typeof firstVal === 'number') {
              acc[col.key] = data.reduce((s, row) => s + (Number(row[col.key]) || 0), 0);
            } else if (currencyMatch) {
              const prefix = currencyMatch[1];
              const suffix = currencyMatch[3];
              const sum = data.reduce((s, row) => {
                const m = String(row[col.key] || '').match(/(-?[\d.]+)/);
                return s + (m ? Number(m[1]) : 0);
              }, 0);
              acc[col.key] = `${prefix}${Math.round(sum * 100) / 100}${suffix}`;
            } else {
              acc[col.key] = '';
            }
          }
          return acc;
        }, {})
      : null;

  return (
    <div className={styles.chartCard}>
      <div className={styles.container}>
        {title ? (
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>
              {title}
              {subTitle ? <span className={styles.chartSubTitle}>{` (${subTitle})`}</span> : null}
            </h3>
            {data && data.length > 0 && (
              <span className={styles.rowCount}>{data.length} records</span>
            )}
          </div>
        ) : null}

        {isEmpty ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyTitle}>No data yet</span>
            <span className={styles.emptyDesc}>
              Transactions will appear here once the campaign is active.
            </span>
          </div>
        ) : (
          <>
            <div className={styles.tableScroll}>
              <table className={styles.rewardTable}>
                <thead>
                  <tr>
                    {headers.map((column, index) => (
                      <th key={index}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((item, rowIndex) => (
                    <tr key={rowIndex}>
                      {headers.map((column, colIndex) => (
                        <td key={colIndex}>
                          {column.isPercentage ? (
                            <div className={styles.percentCell}>
                              <div className={styles.progressTrack}>
                                <div
                                  className={`${styles.progressFill} ${
                                    styles[getProgressColor(item[column.key])]
                                  }`}
                                  style={{ width: `${Math.min(item[column.key], 100)}%` }}
                                />
                              </div>
                              <span className={styles.percentLabel}>
                                {formatPercentage(item[column.key])}
                              </span>
                            </div>
                          ) : (
                            item[column.key]
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className={styles.totalsRow}>
                      {headers.map((column, colIndex) => (
                        <td key={colIndex}>
                          {column.isPercentage ? (
                            <div className={styles.percentCell}>
                              <div className={styles.progressTrack}>
                                <div
                                  className={`${styles.progressFill} ${
                                    styles[getProgressColor(totals[column.key])]
                                  }`}
                                  style={{ width: `${Math.min(totals[column.key], 100)}%` }}
                                />
                              </div>
                              <span className={styles.percentLabel}>
                                {formatPercentage(totals[column.key])}
                              </span>
                            </div>
                          ) : (
                            totals[column.key]
                          )}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <span className={styles.pageInfo}>
                  {page + 1} of {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

Table.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  headers: PropTypes.array,
  data: PropTypes.array,
  showTotals: PropTypes.bool,
};

export default Table;
